// =====================================================================
// Faz AC (2026-05-07) — Drip Campaign Builder
// =====================================================================
// Owner panelden drip kampanya kurabilir, adım ekleyebilir, aktif/pasif
// toggle yapabilir. Backend (drip-runner.js) DB'yi okuyor saatlik
// tetikliyor — UI sadece CRUD.
//
// Sayfa içeriği:
//   - Kampanya listesi (table) — her satır expand olunca step'ler ve
//     son enrollments görünür
//   - "Yeni Kampanya" dialog — name + segment + trigger + ilk step
//   - "Adım Ekle" butonu — var olan kampanyaya yeni step
//
// Endpoint'ler: /api/admin/drip (CRUD)
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Collapse, Divider, Avatar
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import API from "../services/api";
import toast from "react-hot-toast";

// Trigger type → Türkçe etiket
const TRIGGER_LABELS = {
  on_signup:           "Yeni Kayıt",
  on_first_order:      "İlk Sipariş",
  on_segment_assign:   "Segment Atama",
  manual:              "Manuel",
  scheduled:           "Zamanlanmış"
};

// Channel → ikon + renk
const CHANNEL_META = {
  email:    { icon: <EmailIcon />,                color: "#1565C0", label: "Email" },
  sms:      { icon: <SmsIcon />,                  color: "#10b981", label: "SMS" },
  push:     { icon: <NotificationsActiveIcon />,  color: "#f59e0b", label: "Push" },
  whatsapp: { icon: <WhatsAppIcon />,             color: "#25D366", label: "WhatsApp" }
};

// Boş yeni kampanya formu state'i — modal her açılışta sıfırlanır
const emptyCampaign = {
  name: "",
  description: "",
  segment_id: "",
  trigger_type: "manual",
  is_active: true,
  steps: [
    { delay_value: 0, delay_unit: "days", channel: "email", subject: "", body_template: "", notify_template: "" }
  ]
};

const emptyStep = {
  delay_value: 1, delay_unit: "days", channel: "email",
  subject: "", body_template: "", notify_template: ""
};

export default function DripBuilder() {
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Detail expand — hangi kampanyanın step'leri açık?
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});  // id → {steps, subscriptions}

  // Yeni kampanya dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyCampaign);
  const [saving, setSaving] = useState(false);

  // Adım ekleme dialog (var olan kampanyaya)
  const [addStepFor, setAddStepFor] = useState(null);  // campaign_id veya null
  const [stepForm, setStepForm] = useState(emptyStep);

  const load = () => {
    setLoading(true);
    API.get("/api/admin/drip")
      .then(r => {
        setCampaigns(r.data.campaigns || []);
        setSegments(r.data.segments || []);
        setErr(null);
      })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Detay expand — backend'den step'leri çek
  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!details[id]) {
      try {
        const r = await API.get(`/api/admin/drip/${id}`);
        setDetails({ ...details, [id]: r.data });
      } catch (e) {
        toast.error(e.response?.data?.error || e.message);
      }
    }
  };

  // Aktif/pasif toggle
  const toggleActive = async (id) => {
    try {
      await API.put(`/api/admin/drip/${id}/toggle`);
      toast.success("Durum güncellendi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Sil
  const remove = async (id, name) => {
    if (!window.confirm(`"${name}" kampanyası silinecek (tüm adımları + abonelikleri ile). Onaylıyor musun?`)) return;
    try {
      await API.delete(`/api/admin/drip/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Yeni kampanya gönder
  const submitCreate = async () => {
    if (!form.name) { toast.error("Kampanya adı zorunlu"); return; }
    if (form.steps.length === 0) { toast.error("En az 1 adım gerek"); return; }

    setSaving(true);
    try {
      await API.post("/api/admin/drip", {
        ...form,
        segment_id: form.segment_id || null
      });
      toast.success("Kampanya oluşturuldu");
      setCreateOpen(false);
      setForm(emptyCampaign);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Form içinde step ekleme/silme
  const addFormStep = () => setForm({ ...form, steps: [...form.steps, { ...emptyStep, delay_value: form.steps.length }] });
  const removeFormStep = (i) => setForm({ ...form, steps: form.steps.filter((_, idx) => idx !== i) });
  const updateFormStep = (i, field, value) => {
    const newSteps = [...form.steps];
    newSteps[i] = { ...newSteps[i], [field]: value };
    setForm({ ...form, steps: newSteps });
  };

  // Var olan kampanyaya step ekle
  const submitAddStep = async () => {
    try {
      await API.post(`/api/admin/drip/${addStepFor}/steps`, stepForm);
      toast.success("Adım eklendi");
      setAddStepFor(null);
      setStepForm(emptyStep);
      // Detail cache'i temizle
      setDetails({ ...details, [addStepFor]: undefined });
      // Açık ise yeniden çek
      if (expandedId === addStepFor) toggleExpand(addStepFor);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // Adım sil
  const deleteStep = async (stepId, campaignId) => {
    if (!window.confirm("Bu adımı silmek istiyor musun?")) return;
    try {
      await API.delete(`/api/admin/drip/steps/${stepId}`);
      toast.success("Adım silindi");
      setDetails({ ...details, [campaignId]: undefined });
      if (expandedId === campaignId) toggleExpand(campaignId);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  if (loading && campaigns.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Drip Kampanya Yönetimi</Typography>
          <Typography variant="body2" color="text.secondary">
            Çoklu adım otomatik mail/SMS/push akışları
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(emptyCampaign); setCreateOpen(true); }}>
            Yeni Kampanya
          </Button>
        </Box>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* KPI özeti */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Kampanya</Typography>
            <Typography variant="h4" fontWeight={800}>{campaigns.length}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Aktif</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">
              {campaigns.filter(c => c.is_active).length}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Aktif Abonelikler</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">
              {campaigns.reduce((s, c) => s + Number(c.active_subs || 0), 0)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Tamamlanan</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">
              {campaigns.reduce((s, c) => s + Number(c.completed_subs || 0), 0)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Kampanya tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell><strong>Kampanya</strong></TableCell>
              <TableCell><strong>Segment</strong></TableCell>
              <TableCell><strong>Tetikleyici</strong></TableCell>
              <TableCell align="center"><strong>Adım</strong></TableCell>
              <TableCell align="center"><strong>Aktif Abone</strong></TableCell>
              <TableCell align="center"><strong>Durum</strong></TableCell>
              <TableCell align="right"><strong>İşlem</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Henüz kampanya yok. "Yeni Kampanya" ile başla.
                </TableCell>
              </TableRow>
            )}
            {campaigns.map(c => (
              <React.Fragment key={c.id}>
                <TableRow hover sx={{ opacity: c.is_active ? 1 : 0.6 }}>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleExpand(c.id)}>
                      {expandedId === c.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700} fontSize={14}>{c.name}</Typography>
                    {c.description && (
                      <Typography variant="caption" color="text.disabled" fontSize={11}>
                        {c.description.slice(0, 60)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.segment_name ? (
                      <Chip label={c.segment_name} size="small" sx={{ bgcolor: "#e3f2fd" }} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">Tüm kullanıcılar</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={TRIGGER_LABELS[c.trigger_type] || c.trigger_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{c.step_count}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#10b981" }}>{c.active_subs}</TableCell>
                  <TableCell align="center">
                    <Switch checked={!!c.is_active} onChange={() => toggleActive(c.id)} color="success" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Sil">
                      <IconButton size="small" onClick={() => remove(c.id, c.name)} sx={{ color: "#ef4444" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Expand satırı: step'ler + son enrollments */}
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0, borderBottom: "none" }}>
                    <Collapse in={expandedId === c.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: "#f8fafc" }}>
                        {!details[c.id] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            {/* Adımlar */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight={700}>Adımlar ({details[c.id].steps?.length || 0})</Typography>
                              <Button
                                size="small" startIcon={<AddIcon />}
                                onClick={() => { setAddStepFor(c.id); setStepForm(emptyStep); }}
                              >
                                Adım Ekle
                              </Button>
                            </Box>
                            {(details[c.id].steps || []).length === 0 ? (
                              <Alert severity="warning" sx={{ mb: 1 }}>Bu kampanyada adım yok — adım eklemeden tetiklenmez!</Alert>
                            ) : (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                                {details[c.id].steps.map((s) => {
                                  const cm = CHANNEL_META[s.channel] || CHANNEL_META.email;
                                  return (
                                    <Card key={s.id} variant="outlined" sx={{ p: 1.2, display: "flex", alignItems: "center", gap: 1.5 }}>
                                      <Avatar sx={{ bgcolor: cm.color, width: 32, height: 32 }}>{cm.icon}</Avatar>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography fontWeight={700} fontSize={13}>
                                          Adım {s.step_order} · {cm.label}
                                          <Chip label={`+${s.delay_value} ${s.delay_unit}`} size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                                        </Typography>
                                        {s.subject && (
                                          <Typography variant="caption" color="text.secondary" fontSize={11}>
                                            Konu: {s.subject}
                                          </Typography>
                                        )}
                                        {s.notify_template && (
                                          <Typography variant="caption" color="text.disabled" fontSize={10} sx={{ display: "block" }}>
                                            Template: {s.notify_template}
                                          </Typography>
                                        )}
                                      </Box>
                                      <IconButton size="small" onClick={() => deleteStep(s.id, c.id)} sx={{ color: "#ef4444" }}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Card>
                                  );
                                })}
                              </Box>
                            )}

                            {/* Son enrollments */}
                            {details[c.id].subscriptions?.length > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                  Son {details[c.id].subscriptions.length} Abone
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {details[c.id].subscriptions.slice(0, 10).map(s => (
                                    <Chip
                                      key={s.id}
                                      label={`${s.user_name || s.email} · ${s.status} (${s.current_step}/${details[c.id].steps?.length || 0})`}
                                      size="small"
                                      sx={{
                                        bgcolor: s.status === "active" ? "#10b98122" :
                                                s.status === "completed" ? "#1565C022" :
                                                "#f1f5f9",
                                        fontSize: 10
                                      }}
                                    />
                                  ))}
                                </Box>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Yeni Kampanya Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yeni Drip Kampanyası</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField fullWidth label="Kampanya Adı *" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Açıklama" multiline rows={2} value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Hedef Segment" value={form.segment_id}
                onChange={e => setForm({ ...form, segment_id: e.target.value })}
                helperText="Boş = tüm kullanıcılar"
              >
                <MenuItem value="">(Tüm kullanıcılar)</MenuItem>
                {segments.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Tetikleyici *" value={form.trigger_type}
                onChange={e => setForm({ ...form, trigger_type: e.target.value })}
              >
                {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Adımlar — dinamik liste */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>Adımlar</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addFormStep}>Adım Ekle</Button>
          </Box>

          {form.steps.map((s, i) => (
            <Card key={i} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: "#fafbfc" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography fontWeight={700}>Adım {i + 1}</Typography>
                {form.steps.length > 1 && (
                  <IconButton size="small" onClick={() => removeFormStep(i)} sx={{ color: "#ef4444" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={3}>
                  <TextField type="number" fullWidth size="small" label="Bekleme"
                    value={s.delay_value} onChange={e => updateFormStep(i, "delay_value", parseInt(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth size="small" label="Birim" value={s.delay_unit}
                    onChange={e => updateFormStep(i, "delay_unit", e.target.value)}>
                    <MenuItem value="minutes">dakika</MenuItem>
                    <MenuItem value="hours">saat</MenuItem>
                    <MenuItem value="days">gün</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth size="small" label="Kanal" value={s.channel}
                    onChange={e => updateFormStep(i, "channel", e.target.value)}>
                    {Object.entries(CHANNEL_META).map(([k, m]) => (
                      <MenuItem key={k} value={k}>{m.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" label="Template Adı"
                    placeholder="opsiyonel"
                    value={s.notify_template}
                    onChange={e => updateFormStep(i, "notify_template", e.target.value)}
                    helperText="Notification-hub" />
                </Grid>
                {(s.channel === "email") && (
                  <>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Konu"
                        value={s.subject} onChange={e => updateFormStep(i, "subject", e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" multiline rows={2} label="İçerik (HTML/text)"
                        placeholder="Boş bırakırsan template'ten gider"
                        value={s.body_template}
                        onChange={e => updateFormStep(i, "body_template", e.target.value)} />
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adım Ekle Dialog (var olan kampanyaya) */}
      <Dialog open={!!addStepFor} onClose={() => setAddStepFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Adım Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={6}>
              <TextField type="number" fullWidth label="Bekleme" value={stepForm.delay_value}
                onChange={e => setStepForm({ ...stepForm, delay_value: parseInt(e.target.value) || 0 })}
                helperText="Önceki adımdan kaç süre sonra"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Birim" value={stepForm.delay_unit}
                onChange={e => setStepForm({ ...stepForm, delay_unit: e.target.value })}>
                <MenuItem value="minutes">dakika</MenuItem>
                <MenuItem value="hours">saat</MenuItem>
                <MenuItem value="days">gün</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Kanal" value={stepForm.channel}
                onChange={e => setStepForm({ ...stepForm, channel: e.target.value })}>
                {Object.entries(CHANNEL_META).map(([k, m]) => (
                  <MenuItem key={k} value={k}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Template Adı" value={stepForm.notify_template}
                onChange={e => setStepForm({ ...stepForm, notify_template: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Konu" value={stepForm.subject}
                onChange={e => setStepForm({ ...stepForm, subject: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="İçerik" value={stepForm.body_template}
                onChange={e => setStepForm({ ...stepForm, body_template: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStepFor(null)}>İptal</Button>
          <Button variant="contained" onClick={submitAddStep}>Ekle</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
